import type { ComponentType, ReactElement } from 'react';
import React from 'react';
import { MessageDescriptor } from '@lingui/core';
import { Trans } from '@lingui/react';
import styled, { css } from 'styled-components';

import { A11yHidden, DSTokenMap, FocusOutline, MQ } from '@rover/kibble/styles';
import type { Props as CommonProps } from '@rover/react-lib/components/buttons/SelectButton/SelectButton.types';
import CalloutBadge from '@rover/react-lib/components/callouts/CalloutBadge';
import Icon from '@rover/react-lib/components/typography/Icon/Icon';
import { useI18n } from '@rover/rsdk/modules/I18n';

export type Props = CommonProps & {
  allowSelectMultiple: boolean;
  icon?: string | ComponentType;
  title: string | MessageDescriptor;
  label?: string | MessageDescriptor;
  inputGroupName?: string;
  displayAs?: ('compact' | null | undefined) | 'expanded';
  className?: string;
  ariaLabel?: string;
  required: boolean;
};
type StyledLabelProps = {
  selected: boolean;
  hasIcon: boolean;
  disabled?: boolean;
  displayAs?: ('compact' | null | undefined) | 'expanded';
};

const compactLabelStyles = (hasIcon) => `
  justify-content: ${hasIcon ? 'flex-start' : 'center'};
`;

const expandedLabelStyles = `justify-content: center;`;
export const SELECT_BUTTON_IS_SELECTED_CLASS = 'SelectButton--isSelected';
export const SelectButtonLabel = styled.div<StyledLabelProps>`
  padding: 15px 25px;
  margin: 0;
  display: inline-block;
  font-size: 14px;
  box-shadow: inset 0 0 0 ${DSTokenMap.BORDER_WIDTH_PRIMARY.toString()}
    ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY.toString()};
  border-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY.toString()};
  color: ${DSTokenMap.TEXT_COLOR_TERTIARY.toString()};
  border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY.toString()};
  white-space: normal;
  position: relative;
  display: flex;
  flex: 1;
  font-weight: normal;
  user-select: none;
  hyphens: none;
  cursor: pointer;

  &:not(:first-of-type) {
    margin-left: 12px;
  }

  @media (pointer: fine) {
    &:hover {
      color: ${DSTokenMap.TEXT_COLOR_SECONDARY.toString()};
      border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_PRESSED.toString()};
      box-shadow: inset 0 0 0 ${DSTokenMap.BORDER_WIDTH_PRIMARY.toString()}
        ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_PRESSED.toString()};

      // Changes color of icons to match text colors when in hover state
      & > div > div > svg {
        fill: ${DSTokenMap.TEXT_COLOR_SECONDARY.toString()};
      }
      & > div > div > i {
        color: ${DSTokenMap.TEXT_COLOR_SECONDARY.toString()};
      }
      ${({ disabled }) =>
        !disabled
          ? ''
          : css`
              cursor: not-allowed;
              color: ${DSTokenMap.TEXT_COLOR_TERTIARY.toString()};
            `};
    }
  }

  &:active {
    border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE.toString()};
    background-color: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString()};
    box-shadow: inset 0 0 0 2px ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE.toString()};
  }

  ${FocusOutline({
    zIndex: 0,
    outlineOffset: 2,
  })}

  ${({ selected }) =>
    !selected
      ? ''
      : css`
          box-shadow: inset 0 0 0 2px
            ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE.toString()};
          color: ${DSTokenMap.TEXT_COLOR_SECONDARY.toString()};
          border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE.toString()};
          background-color: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString()};

          // Changes color of icons to match text colors when in selected state
          & > div > div > svg {
            fill: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_PRIMARY.toString()};
          }
          & > div > div > i {
            color: ${DSTokenMap.INTERACTIVE_TEXT_COLOR_PRIMARY.toString()};
          }

          &:hover {
            border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE_PRESSED.toString()};
            background-color: ${DSTokenMap.BACKGROUND_COLOR_PRIMARY.toString()};
            box-shadow: inset 0 0 0 2px
              ${DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY_ACTIVE_PRESSED.toString()} !important;
          }
        `}

  ${({ displayAs, hasIcon }) => {
    if (displayAs === 'compact') {
      return compactLabelStyles(hasIcon);
    }

    if (displayAs === 'expanded') {
      return expandedLabelStyles;
    }

    return `
    ${expandedLabelStyles};
    ${MQ.MD_UP} {${compactLabelStyles(hasIcon)}}`;
  }}

  ${({ disabled }) =>
    !disabled
      ? ''
      : css`
          background-color: ${DSTokenMap.BACKGROUND_COLOR_SECONDARY.toString()};
          border-color: ${DSTokenMap.INTERACTIVE_BORDER_COLOR_DISABLED.toString()};
          box-shadow: inset 0 0 0 1px ${DSTokenMap.INTERACTIVE_BORDER_COLOR_DISABLED.toString()};
          color: ${DSTokenMap.TEXT_COLOR_DISABLED.toString()};
        `};
`;
type WrapperProps = {
  displayAs?: ('compact' | null | undefined) | 'expanded';
};
const compactWrapperStyles = `flex-direction: row;`;
const expandedWrapperStyles = `flex-direction: column;`;
export const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;

  ${({ displayAs }) => {
    if (displayAs === 'compact') {
      return compactWrapperStyles;
    }

    if (displayAs === 'expanded') {
      return expandedWrapperStyles;
    }

    return `
    ${expandedWrapperStyles};
    ${MQ.MD_UP} {${compactWrapperStyles}}`;
  }}
`;
type IconWrapperProps = {
  displayAs?: ('compact' | null | undefined) | 'expanded';
};
const compactIconWrapperStyles = `
  margin-bottom: 0;
  margin-right: 10px;`;
const expandedIconWrapperStyles = `margin-bottom: 6px;`;
export const IconWrapper = styled.div<IconWrapperProps>`
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;

  ${({ displayAs }) => {
    if (displayAs === 'compact') {
      return compactIconWrapperStyles;
    }

    if (displayAs === 'expanded') {
      return expandedIconWrapperStyles;
    }

    return `
    ${expandedIconWrapperStyles};
    ${MQ.MD_UP} {${compactIconWrapperStyles}}`;
  }}

  & > svg {
    min-width: 24px;
    max-width: 24px;
    min-height: 24px;
    max-height: 24px;
    fill: ${DSTokenMap.TEXT_COLOR_TERTIARY.toString()};
  }

  & > i {
    font-size: 24px;
    color: ${DSTokenMap.TEXT_COLOR_TERTIARY.toString()};
  }
`;
const StyledHiddenInput = styled.input`
  ${A11yHidden}
`;
type ButtonLabelProps = {
  icon?: boolean;
  displayAs?: ('compact' | null | undefined) | 'expanded';
};

const compactButtonLabelStyles = (icon) => `
  width: 100%;
  text-align: ${icon ? 'left' : 'center'};
`;

const expandedButtonLabelStyles = ` width: 100%; text-align: center;`;
export const ButtonLabel = styled.div<ButtonLabelProps>`
  ${({ displayAs, icon }) => {
    if (displayAs === 'compact') {
      return compactButtonLabelStyles(icon);
    }

    if (displayAs === 'expanded') {
      return expandedButtonLabelStyles;
    }

    return `
    ${expandedButtonLabelStyles};
    ${MQ.MD_UP} {${compactButtonLabelStyles(icon)}}`;
  }}
`;
export const Title = styled.div`
  font-weight: 400;
`;
const StyledCalloutBadge = styled(CalloutBadge)`
  position: absolute;
  top: -4px;
  left: -4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const renderIcon = (ButtonIcon: string | ComponentType) =>
  typeof ButtonIcon === 'string' ? <Icon icon={ButtonIcon} /> : <ButtonIcon />;

const renderSubtitle = (subtitle) => {
  if (!subtitle) return null;
  return (
    <div>
      <Trans id={subtitle} />
    </div>
  );
};

const renderTitle = (title, subtitle, displayAs, icon) => {
  if (!title && !subtitle) return null;
  return (
    <ButtonLabel icon={!!icon} displayAs={displayAs}>
      <Title>
        <Trans id={title} />
      </Title>
      {renderSubtitle(subtitle)}
    </ButtonLabel>
  );
};

const SelectButton = ({
  allowSelectMultiple,
  selected,
  disabled,
  required,
  icon,
  title,
  subtitle,
  label,
  inputGroupName,
  displayAs,
  onClick,
  className,
  ariaLabel,
  ...other
}: Props): ReactElement => {
  const { i18n } = useI18n();
  return (
    <SelectButtonLabel
      disabled={disabled}
      displayAs={displayAs}
      hasIcon={!!icon}
      onClick={!disabled ? onClick : () => {}}
      selected={selected}
      // NOTE(Ryan) This is an eyeroll of a hack but its the quickest/tersest way I could think of
      //            to open up selection styles to modification by consuming components
      className={`${className || ''} ${selected ? SELECT_BUTTON_IS_SELECTED_CLASS : ''}`}
      {...other}
    >
      {label && <StyledCalloutBadge value={label} />}
      <Wrapper displayAs={displayAs}>
        {icon && <IconWrapper displayAs={displayAs}>{renderIcon(icon)}</IconWrapper>}
        {renderTitle(title, subtitle, displayAs, icon)}
        <StyledHiddenInput
          type={allowSelectMultiple ? 'checkbox' : 'radio'}
          name={inputGroupName}
          checked={selected}
          required={required}
          aria-required={required}
          aria-checked={selected}
          // Force title to by of type string since TypeScript has trouble figuring out which overload to use
          // https://github.com/microsoft/TypeScript/issues/14107
          aria-label={ariaLabel || i18n._((title || subtitle) as string)}
          onChange={!disabled ? onClick : () => {}}
        />
      </Wrapper>
    </SelectButtonLabel>
  );
};

SelectButton.defaultProps = {
  value: '',
  allowSelectMultiple: true,
  selected: false,
  disabled: false,
  required: false,
  title: '',
  subtitle: '',
  label: '',
  onClick: () => {},
};

export default SelectButton;
